
import { SidebarProvider } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/AdminSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CodeBlock } from "@/components/api-docs/CodeBlock";
import { API_ENDPOINT } from "@/config/environment";
import { Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const SchoolApiDoc = () => {
  const endpoint = `${API_ENDPOINT}/add-school`;
  
  const exampleRequest = {
    school_id: "12345",
    nama_sekolah: "Example School Name",
    pordi_school_id: "P123" // Optional
  };

  const exampleRequestString = JSON.stringify(exampleRequest, null, 2);
  
  const successResponse = {
    success: true,
    message: "School created successfully",
    data: {
      school: {
        id: "uuid",
        school_id: "12345",
        nama_sekolah: "Example School Name",
        pordi_school_id: "P123",
        created_at: "2025-05-08T12:34:56.789Z"
      }
    }
  };
  
  const errorResponse = {
    success: false,
    error: "School with ID 12345 already exists"
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-gray-100">
        <AdminSidebar />
        <div className="flex-1">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-12">
              <h1 className="text-4xl font-bold" style={{ color: "#008169" }}>
                School API
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                API endpoint for adding new schools to the system
              </p>
            </div>

            <Alert className="mb-8 border-amber-300 bg-amber-50">
              <Info className="h-5 w-5 text-amber-600" />
              <AlertTitle className="text-amber-800">Authentication Required</AlertTitle>
              <AlertDescription className="text-amber-700">
                This API requires authentication using the Supabase anon key as a Bearer token in the Authorization header.
              </AlertDescription>
            </Alert>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-xl flex items-center">
                  API Documentation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Endpoint</h3>
                    <CodeBlock 
                      code={endpoint} 
                      language="text" 
                      title="API Endpoint" 
                    />
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Method</h3>
                    <CodeBlock 
                      code="POST" 
                      language="text" 
                    />
                    <p className="text-sm text-gray-600 mt-2">
                      Note: A GET request to this endpoint will return a message confirming the API is working, along with example usage.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Headers</h3>
                    <CodeBlock 
                      code={`Content-Type: application/json\nAuthorization: Bearer ${API_ENDPOINT.includes('localhost') ? 'your_supabase_anon_key' : '[your_supabase_anon_key]'}`} 
                      language="text" 
                      title="Required Headers" 
                    />
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Request Body</h3>
                    <CodeBlock 
                      code={exampleRequestString} 
                      language="json" 
                      title="Example Request" 
                    />
                    
                    <div className="mt-4 space-y-2">
                      <p className="font-medium">Parameters:</p>
                      <ul className="list-disc pl-5 space-y-2">
                        <li><code>school_id</code>: <span className="text-red-600">Required</span> - Unique identifier for the school</li>
                        <li><code>nama_sekolah</code>: <span className="text-red-600">Required</span> - The name of the school</li>
                        <li><code>pordi_school_id</code>: Optional - External reference ID if needed</li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Success Response (200 OK)</h3>
                    <CodeBlock 
                      code={JSON.stringify(successResponse, null, 2)} 
                      language="json" 
                      title="Success Response" 
                    />
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Error Responses</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium">409 Conflict</h4>
                        <CodeBlock 
                          code={JSON.stringify(errorResponse, null, 2)} 
                          language="json" 
                          title="School Already Exists"
                        />
                      </div>
                      
                      <div>
                        <h4 className="font-medium">400 Bad Request</h4>
                        <CodeBlock 
                          code={JSON.stringify({
                            success: false,
                            error: "School ID is required"
                          }, null, 2)} 
                          language="json" 
                          title="Missing Required Fields"
                        />
                      </div>
                      
                      <div>
                        <h4 className="font-medium">500 Internal Server Error</h4>
                        <CodeBlock 
                          code={JSON.stringify({
                            success: false,
                            error: "Error creating school"
                          }, null, 2)} 
                          language="json" 
                          title="Server Error"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Code Example (JavaScript)</h3>
                    <CodeBlock 
                      code={`// Using fetch API
const response = await fetch("${endpoint}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer your_supabase_anon_key"
  },
  body: JSON.stringify({
    school_id: "12345",
    nama_sekolah: "Example School Name",
    pordi_school_id: "P123" // Optional
  })
});

const result = await response.json();
console.log(result);`} 
                      language="javascript" 
                      title="Fetch API Example" 
                    />
                    
                    <div className="mt-6">
                      <h4 className="font-medium">Testing with cURL</h4>
                      <CodeBlock 
                        code={`curl -X POST "${endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your_supabase_anon_key" \\
  -d '${JSON.stringify(exampleRequest).replace(/"/g, '\\"')}'`} 
                        language="bash" 
                        title="cURL Example" 
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <section className="mt-8">
              <h3 className="text-xl font-bold mb-4">Related APIs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Payment API</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-3">Process payments and update bill statuses</p>
                    <a href="/admin/billing-webhook" className="text-blue-600 hover:underline">View Documentation</a>
                  </CardContent>
                </Card>
                
                <Card className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Subscription Status API</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-3">Check subscription status for schools</p>
                    <a href="/admin/subscription-status-webhook" className="text-blue-600 hover:underline">View Documentation</a>
                  </CardContent>
                </Card>
              </div>
            </section>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default SchoolApiDoc;
